import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import Messenger from '../components/Messenger'
import { chatApi, type PortalMe } from './api'

/**
 * Сообщения в кабинете клиента.
 *
 * Здесь собрана вся переписка: по обращениям, по заявкам и просто разговор
 * с ответственным. Один экран, а не чат в каждом разделе — иначе клиент пишет
 * в одном месте, а мы читаем в другом.
 */
export default function PortalChat({
  me,
  onOpenTicket,
}: {
  me: PortalMe
  onOpenTicket?: (ticketId: number, kind: 'request' | 'order' | null) => void
}) {
  const qc = useQueryClient()
  const [openedId, setOpenedId] = useState<number | null>(null)
  const [error, setError] = useState('')

  const list = useQuery({
    queryKey: ['portal', 'conversations'],
    queryFn: chatApi.list,
    // Собеседник мог ответить минуту назад — список обновляется сам
    refetchInterval: 20_000,
  })

  const opened = useQuery({
    queryKey: ['portal', 'conversation', openedId],
    queryFn: () => chatApi.get(openedId!),
    enabled: openedId !== null,
    refetchInterval: 20_000,
  })

  const staff = useQuery({ queryKey: ['portal', 'chat-staff'], queryFn: chatApi.staff })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['portal', 'conversations'] })
    qc.invalidateQueries({ queryKey: ['portal', 'conversation'] })
    qc.invalidateQueries({ queryKey: ['portal', 'unread'] })
  }

  const start = useMutation({
    mutationFn: (staffId: number) => chatApi.start(staffId),
    onSuccess: (c) => {
      setOpenedId(c.id)
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось начать разговор'),
  })

  async function send(
    body: string,
    file: File | null,
    opts: { replyTo: number | null; forwardOf: number | null },
  ) {
    if (openedId === null) return
    setError('')
    try {
      await chatApi.send(openedId, body, file, opts)
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось отправить')
    }
  }

  async function edit(messageId: number, body: string) {
    if (openedId === null) return
    setError('')
    try {
      await chatApi.editMessage(openedId, messageId, body)
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось изменить')
    }
  }

  const люди = staff.data ?? []

  return (
    <>
      <h1 className="page-title">Сообщения</h1>

      <Messenger
        conversations={list.data ?? []}
        loading={list.isLoading}
        opened={opened.data ?? null}
        openedId={openedId}
        onOpen={setOpenedId}
        onSend={send}
        onEdit={edit}
        onDownload={(messageId, name) => {
          if (openedId === null) return
          void chatApi
            .downloadFile(openedId, messageId, name)
            .catch((e: any) => setError(e?.message ?? 'Не удалось скачать файл'))
        }}
        mineIsClient
        onOpenTicket={onOpenTicket}
        error={error}
        emptyHint="Переписки пока нет"
        header={
          <div className="ms-start">
            <div className="ms-start-title">Написать напрямую</div>
            {люди.length === 0 ? (
              <p className="hint" style={{ margin: 0 }}>
                За вашей организацией пока никто не закреплён. Напишите нам через
                обращение — мы назначим ответственного.
              </p>
            ) : (
              люди.map((p) => (
                <button
                  key={p.id}
                  className="btn small"
                  style={{ marginRight: 6, marginTop: 6 }}
                  disabled={start.isPending}
                  onClick={() => start.mutate(p.id)}
                  title={`${p.role}, ведёт ${me.client_name}`}
                >
                  {p.full_name}
                </button>
              ))
            )}
          </div>
        }
      />
    </>
  )
}
